import { createStyles, UnstyledButton, UnstyledButtonProps } from "@mantine/core";
import { PolymorphicComponentProps } from "@mantine/utils";
import React from "react";
import { IconColor, IconFn, IconFnProps } from "../../icons/createIcon";

interface RpiIconButtonStyleProps {
  disableAnimation?: boolean;
}

type IconPropsKeys = "color" | "size" | "width" | "height";

type RpiIconButtonIconProps<C extends IconColor, P extends Record<string, unknown>> = {
  icon: IconFn<C, P>;
  iconProps?: Partial<IconFnProps<C, P>>;
} & Pick<IconFnProps<C, P>, IconPropsKeys>;

const useRpiIconButtonStyles = createStyles<"root", Pick<RpiIconButtonStyleProps, "disableAnimation">>(
  (_, { disableAnimation }) => {
    return {
      root: {
        "&:disabled": {
          cursor: "default !important",
        },

        ...(!disableAnimation && {
          "&:active": {
            transform: "translateY(1px)",
          },
          "&:disabled": {
            transform: "none !important",
          },
        }),
      },
    };
  }
);

type RpiIconButtonProps<C extends IconColor, P extends Record<string, unknown>, T = "button"> = RpiIconButtonIconProps<
  C,
  P
> &
  Omit<PolymorphicComponentProps<T, UnstyledButtonProps>, "children" | IconPropsKeys> &
  RpiIconButtonStyleProps;

export function RpiIconButton<C extends IconColor, P extends Record<string, unknown>, T = "button">({
  icon,
  iconProps = {},
  color,
  size,
  width,
  height,

  disableAnimation,

  className,
  customRef,

  ...props
}: RpiIconButtonProps<C, P, T> & { customRef?: React.ForwardedRef<any> }): React.ReactElement {
  const { classes, cx } = useRpiIconButtonStyles({ disableAnimation });

  return (
    // @ts-expect-error legacy type issues
    <UnstyledButton
      ref={customRef}
      className={cx(classes.root, className)}
      children={React.createElement(icon, { ...iconProps, color, size, width, height } as IconFnProps<C, P>)}
      {...props}
    />
  );
}
